import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Stack,
  Divider,
  Checkbox,
  ListItemText,
  ListItemIcon,
  ListSubheader,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import SaveIcon from '@mui/icons-material/Save';
import FilterListIcon from '@mui/icons-material/FilterList';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Menu from '@mui/material/Menu';
import MenuItemMUI from '@mui/material/MenuItem';
import { CheckCircleOutline, HighlightOffOutlined } from '@mui/icons-material';
import { getTeams } from '~/services/teamService';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useNavigate } from 'react-router-dom';

interface ATeam {
  id: number;
  name: string;
  numberOfPlayers: number;
  active: boolean;
}

interface SearchFilters {
  searchText: string;
  activeFilter: boolean | undefined;
}

function ListTeams() {
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = useState('');
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>(true);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<{
    [key: string]: boolean;
  }>({
    id: false,
    name: true,
    numberOfPlayers: true,
    active: true,
    actions: true,
  });
  const [savedSearches, setSavedSearches] = useState<string[]>([]);
  const [filtersAnchorEl, setFiltersAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [columnsAnchorEl, setColumnsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [teams, setTeams] = useState<ATeam[]>([]);
  const open = Boolean(anchorEl);
  const [selectedRowId, setSelectedRowId] = React.useState<
    string | number | null
  >(null);

  const handleClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: string | number
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(id);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const isFiltersMenuOpen = Boolean(filtersAnchorEl);
  const isColumnsMenuOpen = Boolean(columnsAnchorEl);

  const handleViewDetails = () => {
    if (selectedRowId !== null) {
      navigate(`/teams/${selectedRowId}`);
    }
    handleClose();
  };

  const handleEdit = () => {
    if (selectedRowId !== null) {
      navigate(`/teams/${selectedRowId}/edit`);
    }
    handleClose();
  };

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const fetchedTeams = await getTeams({ organizationId: '100' });
        const newTeams = fetchedTeams.map(team => ({
          id: parseInt(team.id, 10),
          name: team.name,
          active: team.active,
          numberOfPlayers: Math.floor(Math.random() * 20) + 1,
        }));

        setTeams(newTeams);
      } catch (error) {
        console.error('Error fetching teams:', error);
      }
    };

    fetchTeams();
  }, []);

  const filteredTeams = useMemo(() => {
    return teams.filter(team => {
      return searchText
        ? team.name.toLowerCase().includes(searchText.toLowerCase()) ||
            team.numberOfPlayers.toString().includes(searchText)
        : true;
    });
  }, [teams, searchText, activeFilter]);

  const columns: GridColDef[] = [
    { field: 'name', headerName: 'Name', flex: 1 },
    {
      field: 'numberOfPlayers',
      headerName: 'Player Count',
      width: 100,
      filterable: false,
      align: 'center',
    },
    {
      field: 'active',
      align: 'center',
      headerName: 'Status',
      width: 120,
      renderCell: (params: { row: ATeam }) => (
        <Box display="flex" alignItems="center" height={52}>
          {params.row.active ? (
            <CheckCircleOutline color="success" />
          ) : (
            <HighlightOffOutlined color="error" />
          )}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      align: 'left',
      headerAlign: 'left',
      width: 110,
      type: 'actions',
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams) => (
        <>
          <IconButton
            aria-label="actions"
            aria-controls={open ? 'actions-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            onClick={event => handleClick(event, params.row.id)}
            size="small"
          >
            <MoreHorizIcon fontSize="small" />
          </IconButton>
          <Menu
            id="actions-menu"
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'actions-button',
            }}
          >
            <MenuItem
              onClick={handleViewDetails}
              disabled={selectedRowId === null}
            >
              <VisibilityIcon sx={{ mr: 1 }} fontSize="small" /> Details
            </MenuItem>
            <MenuItem onClick={handleEdit} disabled={selectedRowId === null}>
              <EditIcon sx={{ mr: 1 }} fontSize="small" /> Edit
            </MenuItem>
            {/* Add more MenuItem components for other actions */}
          </Menu>
        </>
      ),
    },
    // Add more columns as needed
  ];
  const visibleColumns = useMemo(() => {
    return columns.filter(col => columnVisibilityModel[col.field]);
  }, [columns, columnVisibilityModel]);

  const handleSaveSearch = () => {
    const currentFilters: SearchFilters = { searchText, activeFilter };
    const searchString = JSON.stringify(currentFilters);
    if (!savedSearches.includes(searchString)) {
      setSavedSearches([...savedSearches, searchString]);
    }
  };

  const handleLoadSearch = (search: string) => {
    const filters: SearchFilters = JSON.parse(search);
    setSearchText(filters.searchText || '');
    setActiveFilter(filters.activeFilter || undefined);
  };

  const handleClearFilters = () => {
    setSearchText('');
    setActiveFilter(undefined);
  };

  const handleColumnVisibilityChange = (field: string) => {
    setColumnVisibilityModel(prev => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleOpenFiltersMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setFiltersAnchorEl(event.currentTarget);
  };

  const handleCloseFiltersMenu = () => {
    setFiltersAnchorEl(null);
  };

  const handleOpenColumnsMenu = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleCloseColumnsMenu = () => {
    setColumnsAnchorEl(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
        }}
      >
        <Typography variant="h6">Team Search</Typography>
        <Button variant="contained" color="primary">
          Create Team
        </Button>
      </Box>

      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <Button
          startIcon={<SaveIcon />}
          size="small"
          onClick={handleSaveSearch}
          variant="outlined"
        >
          Save Search
        </Button>
        {savedSearches.length > 0 && (
          <>
            <FormControl size="small">
              <InputLabel id="saved-searches-label">Saved Searches</InputLabel>
              <Select
                labelId="saved-searches-label"
                id="saved-searches"
                value="" // You might want to manage a selected saved search
                label="Saved Searches"
              >
                {savedSearches.map((search, index) => (
                  <MenuItem
                    key={index}
                    value={search}
                    onClick={() => handleLoadSearch(search)}
                  >
                    Search {index + 1}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} mb={2}>
        <Button
          startIcon={<FilterListIcon />}
          onClick={handleOpenFiltersMenu}
          size="small"
          variant="outlined"
        >
          Filters
        </Button>
        <Button
          startIcon={<ViewColumnIcon />}
          onClick={handleOpenColumnsMenu}
          size="small"
          variant="outlined"
        >
          Columns
        </Button>
        <TextField
          label="Search name"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          size="small"
          sx={{ flexGrow: 1 }}
        />
        <Button onClick={handleClearFilters} size="small">
          Clear Filters
        </Button>
      </Stack>

      <Menu
        anchorEl={filtersAnchorEl}
        open={isFiltersMenuOpen}
        onClose={handleCloseFiltersMenu}
        PaperProps={{
          style: {
            width: 250,
          },
        }}
      >
        <ListSubheader>Filter By</ListSubheader>
        <Divider />
        <MenuItem>
          <FormControl fullWidth size="small">
            <InputLabel id="year-born-filter-label">Year Born</InputLabel>
            <Select
              labelId="year-born-filter-label"
              id="year-born-filter"
              value={activeFilter}
              label="Year Born"
              onChange={e => setActiveFilter(e.target.value === 'active')}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem key="true" value="active">
                Active
              </MenuItem>
              <MenuItem key="false" value="inactive">
                InActive
              </MenuItem>
            </Select>
          </FormControl>
        </MenuItem>
        {/* Add more filter options here if needed */}
      </Menu>

      <Menu
        anchorEl={columnsAnchorEl}
        open={isColumnsMenuOpen}
        onClose={handleCloseColumnsMenu}
        PaperProps={{
          style: {
            width: 200,
          },
        }}
      >
        <ListSubheader>Show Columns</ListSubheader>
        <Divider />
        {columns.map(col => (
          <MenuItemMUI
            key={col.field}
            onClick={() => handleColumnVisibilityChange(col.field)}
          >
            <ListItemIcon>
              <Checkbox
                checked={columnVisibilityModel[col.field] || false}
                edge="start"
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={col.headerName} />
          </MenuItemMUI>
        ))}
      </Menu>

      <div style={{ height: '65vh', width: '100%' }}>
        <DataGrid
          rows={filteredTeams}
          columns={visibleColumns}
          pageSizeOptions={[5, 10, 20]}
          disableRowSelectionOnClick
        />
      </div>
    </Box>
  );
}

export default ListTeams;
